import React, { ReactNode, useState } from 'react';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import {
    CheckCircleIcon,
    CTableWrapper,
    DModal,
    DPageLoader,
    ViewIcon,
} from '../../../../../dum';
import { OrderQuotationIcon } from '../../../../icons';
import {
    clearSalesModule,
    OrderQuotationCatalogAction,
    OrderQuotationPedidoStepState,
    useAcceptOrderQuotationMutation,
    useOrderQuotationAcceptanceQuery,
} from '../../../../../../services';
import {
    formatFloat,
    getBusinessDateFromInstant,
} from '../../../../../../helpers';

interface ISAcceptOrderQuotationDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    orderQuotationId: number | null;
}

// A catalog line uses ONE price type (kilo or bulto). Show whichever is
// non-zero; null on both sides of `current` means the product is not in the
// client's catalog yet (an AGREGAR row).
function priceText(
    kilo: number | null | undefined,
    group: number | null | undefined,
): string {
    if (kilo == null && group == null) return 'No existe';
    const k = kilo || 0;
    const g = group || 0;
    if (g !== 0 && k === 0) return `$${formatFloat(g)}/bulto`;
    return `$${formatFloat(k)}/kg`;
}

// The per-line importe. There is no server-side `total` ResolveField on
// OrderQuotationProduct, so it is the exact formula the backend uses for the
// quotation subtotal (kilo_price·kilos + group_price·groups). A line carries one
// price or the other. Mirrors printable-quotation.tsx so the two previews agree.
function lineTotal(orp: {
    kilo_price: number;
    kilos: number;
    group_price: number;
    groups: number;
}): number {
    return orp.kilo_price * orp.kilos + orp.group_price * orp.groups;
}

// The per-line unit price, ALWAYS per kilo so the column is unambiguous. A
// kilo-priced line uses kilo_price; a group-priced line is converted to its
// per-kg equivalent (importe / kilos). Mirrors printable-quotation.tsx so the
// two previews agree.
function unitPricePerKilo(orp: {
    kilo_price: number;
    kilos: number;
    group_price: number;
    groups: number;
}): number {
    if (orp.kilo_price) {
        return orp.kilo_price;
    }
    return orp.kilos > 0 ? lineTotal(orp) / orp.kilos : orp.group_price;
}

// Bultos / Kilos show no decimals when integral, two when fractional, so a
// fractional weight is never silently dropped.
function formatQty(x: number): string {
    return Number.isInteger(x) ? formatFloat(x, 0) : formatFloat(x, 2);
}

function StepChip({ label, done }: { label: string; done?: boolean | null }) {
    return (
        <Chip
            size={'small'}
            color={done ? 'success' : 'default'}
            variant={done ? 'filled' : 'outlined'}
            icon={done ? <CheckCircleIcon /> : undefined}
            label={`${label}: ${done ? 'hecho' : 'pendiente'}`}
        />
    );
}

function ActionChip({ action }: { action: OrderQuotationCatalogAction }) {
    const map: Record<
        OrderQuotationCatalogAction,
        { color: 'success' | 'warning' | 'default'; label: string }
    > = {
        [OrderQuotationCatalogAction.Agregar]: {
            color: 'success',
            label: 'Agregar',
        },
        [OrderQuotationCatalogAction.Actualizar]: {
            color: 'warning',
            label: 'Actualizar',
        },
        [OrderQuotationCatalogAction.SinCambio]: {
            color: 'default',
            label: 'Sin cambio',
        },
    };
    const { color, label } = map[action];
    return (
        <Chip size={'small'} color={color} variant={'outlined'} label={label} />
    );
}

// The AGREGAR / ACTUALIZAR / SIN_CAMBIO preview: current catalog price → quoted
// price per product. Reused in the dialog body (the overview) AND inside the
// confirmation, so it is a single component rather than two copies of the same
// column list. `changes` is the getOrderQuotationCatalogChanges.changes array;
// content params stay unannotated (Record<any,any>) per the codebase's table
// convention.
function CatalogChangesTable({ changes }: { changes: any[] }) {
    return (
        <CTableWrapper
            padding={false}
            items={[
                {
                    title: 'Producto',
                    cellGrow: 40,
                    content: (c) => c.description,
                },
                {
                    title: 'Precio actual',
                    cellGrow: 20,
                    textAlign: 'right',
                    content: (c) =>
                        priceText(c.current_kilo_price, c.current_group_price),
                },
                {
                    title: (
                        <Box component={'span'} sx={{ ml: 3 }}>
                            Precio cotización
                        </Box>
                    ),
                    cellGrow: 20,
                    textAlign: 'right',
                    cellSx: () => ({ pl: 3 }),
                    content: (c) =>
                        priceText(
                            c.proposed_kilo_price,
                            c.proposed_group_price,
                        ),
                },
                {
                    title: (
                        <Box component={'span'} sx={{ ml: 3 }}>
                            Acción
                        </Box>
                    ),
                    cellGrow: 20,
                    cellSx: () => ({ pl: 3 }),
                    content: (c) => <ActionChip action={c.action} />,
                },
            ]}
            data={changes}
        />
    );
}

// The pedido that acceptance will create, previewed from the quotation's own
// lines (built verbatim from them). order_code shows "(se generará)" because the
// code is generated at acceptance, and the date is the acceptance date (today).
// A free line renders its proposed_description; a linked line the product's
// external_description. Totals use the sum of the shown line importes so the
// footer always matches the rows above it.
function OrderRequestPreview({
    products,
    productDescriptions,
}: {
    products: any[];
    productDescriptions: Map<number, string>;
}) {
    const subtotal = products.reduce((sum, orp) => sum + lineTotal(orp), 0);
    return (
        <CTableWrapper
            padding={false}
            items={[
                {
                    title: 'Producto / especificación',
                    cellGrow: 40,
                    content: (orp) => {
                        const isFreeLine = orp.product_id == null;
                        const description = isFreeLine
                            ? orp.proposed_description
                            : orp.product?.external_description ||
                              productDescriptions.get(orp.product_id) ||
                              `Producto #${orp.product_id}`;

                        return isFreeLine ? (
                            <Box component={'span'}>
                                {description}
                                <Box
                                    component={'span'}
                                    sx={{
                                        ml: 0.5,
                                        color: 'text.secondary',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    (línea libre)
                                </Box>
                            </Box>
                        ) : (
                            description
                        );
                    },
                },
                {
                    title: 'Bultos',
                    cellGrow: 12,
                    textAlign: 'right',
                    content: (orp) => formatQty(orp.groups),
                },
                {
                    title: 'Kilos',
                    cellGrow: 12,
                    textAlign: 'right',
                    content: (orp) => formatQty(orp.kilos),
                },
                {
                    title: 'Precio kg',
                    cellGrow: 16,
                    textAlign: 'right',
                    content: (orp) => `$${formatFloat(unitPricePerKilo(orp))}`,
                },
                {
                    title: 'Importe',
                    cellGrow: 20,
                    textAlign: 'right',
                    content: (orp) => `$${formatFloat(lineTotal(orp))}`,
                },
            ]}
            data={products}
            totalsRow={['Total', null, null, null, `$${formatFloat(subtotal)}`]}
        />
    );
}

// The four consequences of accepting, stated plainly. This is the safeguard that
// stands in for reversibility (the plan: "Acceptance is terminal — no revert"),
// so it names every consequence rather than asking "¿está seguro?".
function IrreversibilityCopy() {
    return (
        <Alert severity={'warning'}>
            <Typography variant={'body2'} sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Al aceptar esta cotización:
            </Typography>
            <Box component={'ul'} sx={{ m: 0, pl: 2.5 }}>
                <li>
                    Se <b>modifica la lista de precios del cliente</b> (se
                    agregan y actualizan productos en su catálogo).
                </li>
                <li>
                    Se <b>crea el pedido</b> y la cotización queda marcada como{' '}
                    <b>Aceptada</b>.
                </li>
                <li>
                    La acción <b>no se puede revertir</b>: el vínculo cotización
                    → pedido es único e inmutable.
                </li>
                <li>
                    Si el pedido se elimina después,{' '}
                    <b>no se puede regenerar</b> desde esta cotización; habría
                    que capturar una cotización nueva.
                </li>
            </Box>
        </Alert>
    );
}

function SectionTitle({ children }: { children: ReactNode }) {
    return (
        <Typography variant={'subtitle2'} sx={{ mb: 1 }}>
            {children}
        </Typography>
    );
}

// The DIAGNOSTIC surface (read-only). Describes each acceptance step's state in
// plain Spanish and, when the pipeline is incomplete, warns the user. It never
// exposes stack traces or internals — the detailed exception stays in the
// backend logs. Supports ANY incomplete combination of steps (failures are not
// assumed to occur in order). See the plan, "Add a simple acceptance
// diagnostics tab".
function AcceptanceDiagnostics({ quotation }: { quotation: any }) {
    const pedidoState: OrderQuotationPedidoStepState =
        quotation.order_request_step_state;
    const linkedPedido = quotation.order_request;

    const catalogDone = !!quotation.catalog_step_done;
    const statusDone = !!quotation.status_step_done;
    const pedidoIncomplete =
        pedidoState === OrderQuotationPedidoStepState.CreatedIncomplete;
    const complete = !!quotation.acceptance_complete;
    const incompletePipeline =
        !complete &&
        (catalogDone ||
            pedidoState !== OrderQuotationPedidoStepState.NotCreated);

    const pedidoText =
        pedidoState === OrderQuotationPedidoStepState.Completed
            ? `Completado${
                  linkedPedido ? ` · pedido #${linkedPedido.order_code}` : ''
              }`
            : pedidoState === OrderQuotationPedidoStepState.CreatedIncomplete
            ? `Creado pero incompleto${
                  linkedPedido ? ` · pedido #${linkedPedido.order_code}` : ''
              }`
            : 'No creado';

    return (
        <Box>
            <SectionTitle>Diagnóstico de la aceptación</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant={'body2'}>
                    <b>Catálogo:</b> {catalogDone ? 'Completado' : 'Pendiente'}
                </Typography>
                <Typography variant={'body2'}>
                    <b>Pedido:</b> {pedidoText}
                </Typography>
                <Typography variant={'body2'}>
                    <b>Estado de la cotización:</b>{' '}
                    {statusDone ? 'Aceptada' : 'Pendiente'}
                </Typography>
            </Box>

            {pedidoIncomplete ? (
                <Alert severity={'error'} sx={{ mt: 1 }}>
                    <Typography variant={'body2'} sx={{ fontWeight: 'bold' }}>
                        El pedido existe pero su creación no se marcó como
                        completada.
                    </Typography>
                    <Typography variant={'body2'}>
                        {linkedPedido
                            ? `Pedido #${linkedPedido.order_code}. `
                            : ''}
                        No se creará otro pedido ni se modificará éste
                        automáticamente. Espera a que se diagnostique el
                        problema de software antes de continuar.
                    </Typography>
                </Alert>
            ) : incompletePipeline ? (
                <Alert severity={'warning'} sx={{ mt: 1 }}>
                    La aceptación quedó incompleta. Puede continuarse cuando el
                    problema se haya resuelto; los pasos ya completados no se
                    repiten.
                </Alert>
            ) : null}
        </Box>
    );
}

// A single field diff for the comparison. Shows one value when equal, "q → p"
// when they differ.
function diffPair(q: number, p: number): { text: string; differs: boolean } {
    const differs = Number(q) !== Number(p);
    return {
        text: differs
            ? `${formatFloat(q)} → ${formatFloat(p)}`
            : formatFloat(q),
        differs,
    };
}

// The read-only quotation-vs-pedido comparison (informational only). Rendered
// only when a linked pedido exists. It NEVER blocks acceptance, updates either
// record, or reconciles anything — after a pedido is completed, users edit it
// through its normal workflow and the quotation stays a historical record. Lines
// are joined by product_id; a line present on only one side is flagged as
// missing/additional. See the plan, "Add a read-only quotation versus pedido
// comparison".
function QuotationVsPedidoComparison({
    quotationLines,
    pedido,
    productDescriptions,
}: {
    quotationLines: any[];
    pedido: any;
    productDescriptions: Map<number, string>;
}) {
    const pedidoLines = pedido?.order_request_products ?? [];
    const qByProduct = new Map<number, any>();
    quotationLines
        .filter((l) => l.product_id != null)
        .forEach((l) => qByProduct.set(l.product_id, l));
    const pByProduct = new Map<number, any>();
    pedidoLines.forEach((l: any) => pByProduct.set(l.product_id, l));

    const productIds = Array.from(
        new Set<number>([
            ...Array.from(qByProduct.keys()),
            ...Array.from(pByProduct.keys()),
        ]),
    );

    const rows = productIds.map((productId) => {
        const q = qByProduct.get(productId);
        const p = pByProduct.get(productId);
        const description =
            q?.product?.external_description ||
            p?.product?.external_description ||
            productDescriptions.get(productId) ||
            `Producto #${productId}`;
        return { productId, description, q, p };
    });

    return (
        <Box>
            <SectionTitle>Comparación cotización vs pedido</SectionTitle>
            <Typography
                variant={'caption'}
                color={'text.secondary'}
                display={'block'}
                sx={{ mb: 1 }}
            >
                Solo informativo. Las diferencias no bloquean nada: después de
                completarse, el pedido se edita por su flujo normal y la
                cotización queda como registro histórico.
            </Typography>
            {pedido?.account ? (
                <Typography
                    variant={'body2'}
                    color={'text.secondary'}
                    sx={{ mb: 1 }}
                >
                    Cuenta del pedido: {pedido.account.name}
                </Typography>
            ) : null}
            <CTableWrapper
                padding={false}
                items={[
                    {
                        title: 'Producto',
                        cellGrow: 34,
                        content: (r) => r.description,
                    },
                    {
                        title: 'Bultos',
                        cellGrow: 12,
                        textAlign: 'right',
                        content: (r) =>
                            !r.q
                                ? '—'
                                : !r.p
                                ? formatFloat(r.q.groups)
                                : diffPair(r.q.groups, r.p.groups).text,
                    },
                    {
                        title: 'Kilos',
                        cellGrow: 12,
                        textAlign: 'right',
                        content: (r) =>
                            !r.q
                                ? '—'
                                : !r.p
                                ? formatFloat(r.q.kilos)
                                : diffPair(r.q.kilos, r.p.kilos).text,
                    },
                    {
                        title: 'Peso bulto',
                        cellGrow: 12,
                        textAlign: 'right',
                        content: (r) =>
                            !r.q
                                ? '—'
                                : !r.p
                                ? formatFloat(r.q.group_weight)
                                : diffPair(r.q.group_weight, r.p.group_weight)
                                      .text,
                    },
                    {
                        title: 'Precio kg',
                        cellGrow: 12,
                        textAlign: 'right',
                        content: (r) =>
                            !r.q
                                ? '—'
                                : !r.p
                                ? formatFloat(r.q.kilo_price)
                                : diffPair(r.q.kilo_price, r.p.kilo_price).text,
                    },
                    {
                        title: 'Precio bulto',
                        cellGrow: 12,
                        textAlign: 'right',
                        content: (r) =>
                            !r.q
                                ? '—'
                                : !r.p
                                ? formatFloat(r.q.group_price)
                                : diffPair(r.q.group_price, r.p.group_price)
                                      .text,
                    },
                    {
                        title: 'Estado',
                        cellGrow: 16,
                        content: (r) =>
                            !r.p ? (
                                <Chip
                                    size={'small'}
                                    color={'warning'}
                                    variant={'outlined'}
                                    label={'Falta en pedido'}
                                />
                            ) : !r.q ? (
                                <Chip
                                    size={'small'}
                                    color={'info'}
                                    variant={'outlined'}
                                    label={'Adicional en pedido'}
                                />
                            ) : (
                                <Chip
                                    size={'small'}
                                    variant={'outlined'}
                                    label={'Vinculado'}
                                />
                            ),
                    },
                ]}
                data={rows}
            />
        </Box>
    );
}

// The acceptance screen. It is NOT a form: it previews what accepting will do to
// the client's catalog (getOrderQuotationCatalogChanges), shows the three-step
// panel (Catálogo · Pedido · Estado, derived + one stored), and gates the
// destructive acceptOrderQuotation behind an irreversibility confirmation that
// previews BOTH changes (catalog + the pedido) before the terminal commit. After
// a partial failure the same button resumes (Continuar aceptación) — the service
// skips whatever is already done.
export function SAcceptOrderQuotationDialog({
    open,
    setOpen,
    orderQuotationId,
}: ISAcceptOrderQuotationDialogProps) {
    // The user-friendly error from an acceptance attempt IN THIS SESSION. Shown
    // in the diagnostics area; no stack trace or internals. See the plan, "Add a
    // simple acceptance diagnostics tab".
    const [sessionError, setSessionError] = useState<string | null>(null);

    const { data, loading, refetch } = useOrderQuotationAcceptanceQuery({
        skip: !orderQuotationId || !open,
        variables: {
            OrderQuotationIdInt: orderQuotationId!,
            OrderQuotationIdFloat: orderQuotationId!,
        },
        // The catalog may have changed since the dialog was last opened; a
        // human-driven resume must re-read the preview, not trust the cache.
        fetchPolicy: 'cache-and-network',
    });

    const [acceptMutation, { loading: accepting }] =
        useAcceptOrderQuotationMutation({
            update: (cache) => {
                clearSalesModule(cache);
            },
        });

    const preview = data?.getOrderQuotationCatalogChanges;
    const quotation = data?.getOrderQuotation;

    const isAcceptable = !!preview?.is_acceptable;
    const complete = !!quotation?.acceptance_complete;
    // A partially-applied acceptance: at least one step done but not all. The
    // button then reads "Continuar aceptación" instead of "Aceptar".
    const anyStepDone = !!(
        quotation?.catalog_step_done || quotation?.order_request_step_done
    );
    const buttonLabel = anyStepDone ? 'Continuar aceptación' : 'Aceptar';

    // The pedido half-state: a pedido row exists but its completion was never
    // stamped. Acceptance is disabled and an engineer diagnoses it; the app never
    // creates a second pedido or repairs this one.
    const pedidoIncomplete =
        quotation?.order_request_step_state ===
        OrderQuotationPedidoStepState.CreatedIncomplete;

    const products = quotation?.order_quotation_products ?? [];
    const productDescriptions = new Map(
        (data?.getProducts ?? []).map((product) => [
            product.id,
            product.external_description,
        ]),
    );

    const handleAccept = async () => {
        if (!orderQuotationId) return;
        setSessionError(null);
        try {
            await acceptMutation({
                variables: { OrderQuotationId: orderQuotationId },
            });
            setOpen(false);
        } catch (e: any) {
            // A user-friendly message only — surfaced in the diagnostics area.
            setSessionError(
                e?.message ||
                    'No se pudo completar la aceptación. Revisa el diagnóstico.',
            );
            // Always refetch after an attempt so the steps/diagnostic reflect what
            // actually happened (e.g. catalog written but pedido not), never
            // stale state. See the plan, "Make the acceptance dialog reflect
            // pending work".
            try {
                await refetch();
            } catch {
                // A failed refetch is non-fatal; the error above still shows.
            }
        }
    };

    return (
        <DModal
            icon={
                quotation?.status_step_done ? (
                    <ViewIcon />
                ) : (
                    <OrderQuotationIcon />
                )
            }
            title={
                quotation?.status_step_done
                    ? 'Vista de aceptación'
                    : 'Aceptar cotización'
            }
            open={open}
            setOpen={setOpen}
        >
            {loading ? (
                <DPageLoader />
            ) : !quotation || !preview ? null : (
                // One dialog only. The old flow popped a second "Confirmar
                // aceptación" modal for the irreversibility warning + previews;
                // that is merged here so the whole acceptance lives on a single
                // screen, with the destructive action pinned to a bottom toolbar
                // like the upsert forms.
                <>
                    <Box
                        sx={{
                            flex: 1,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant={'subtitle1'}>
                                Cotización #{quotation.order_code}
                            </Typography>
                            <Typography
                                variant={'body2'}
                                color={'text.secondary'}
                            >
                                {quotation.account?.name}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <StepChip
                                label={'Catálogo'}
                                done={quotation.catalog_step_done}
                            />
                            <StepChip
                                label={'Pedido'}
                                done={quotation.order_request_step_done}
                            />
                            <StepChip
                                label={'Estado'}
                                done={quotation.status_step_done}
                            />
                        </Box>

                        {complete ? (
                            <Alert severity={'success'}>
                                La cotización ya fue aceptada
                                {quotation.order_request
                                    ? ` · pedido #${quotation.order_request.order_code}`
                                    : ''}
                                .
                            </Alert>
                        ) : (
                            <>
                                {/* Describe only what is STILL PENDING. Completed
                                    steps are stated as already done and not
                                    repeated. See the plan, "Make the acceptance
                                    dialog reflect pending work". */}
                                <Alert severity={'warning'}>
                                    <Typography
                                        variant={'body2'}
                                        sx={{ mb: 0.5 }}
                                    >
                                        Al continuar se ejecutará únicamente lo
                                        pendiente:
                                    </Typography>
                                    <Box
                                        component={'ul'}
                                        sx={{ m: 0, pl: 2.5 }}
                                    >
                                        {quotation.catalog_step_done ? (
                                            <li>
                                                El catálogo ya se actualizó y{' '}
                                                <b>no se repetirá</b>.
                                            </li>
                                        ) : (
                                            <li>
                                                Se{' '}
                                                <b>
                                                    actualizará la lista de
                                                    precios
                                                </b>{' '}
                                                del cliente (ver los cambios
                                                abajo).
                                            </li>
                                        )}
                                        {quotation.order_request_step_state ===
                                        OrderQuotationPedidoStepState.Completed ? (
                                            <li>
                                                El pedido ya existe
                                                {quotation.order_request
                                                    ? ` (#${quotation.order_request.order_code})`
                                                    : ''}{' '}
                                                y <b>no se creará de nuevo</b>.
                                            </li>
                                        ) : pedidoIncomplete ? (
                                            <li>
                                                El pedido existe pero su
                                                creación quedó incompleta —{' '}
                                                <b>
                                                    la aceptación está bloqueada
                                                </b>{' '}
                                                hasta diagnosticarlo.
                                            </li>
                                        ) : (
                                            <li>
                                                Se <b>creará el pedido</b>.
                                            </li>
                                        )}
                                        {quotation.status_step_done ? (
                                            <li>El estado ya es Aceptada.</li>
                                        ) : (
                                            <li>
                                                Se marcará la cotización como{' '}
                                                <b>Aceptada</b>.
                                            </li>
                                        )}
                                    </Box>
                                </Alert>

                                {/* The four consequences of accepting, in full.
                                    Merged in from the removed confirmation modal
                                    so the single dialog still states every
                                    irreversible effect before the commit. */}
                                <IrreversibilityCopy />
                            </>
                        )}

                        <AcceptanceDiagnostics quotation={quotation} />

                        {sessionError ? (
                            <Alert severity={'error'}>{sessionError}</Alert>
                        ) : null}

                        <Box>
                            <SectionTitle>Cambios al catálogo</SectionTitle>
                            <CatalogChangesTable changes={preview.changes} />
                        </Box>

                        {quotation.order_request ? (
                            <QuotationVsPedidoComparison
                                quotationLines={products}
                                pedido={quotation.order_request}
                                productDescriptions={productDescriptions}
                            />
                        ) : (
                            // No pedido yet: preview the one acceptance will
                            // create (was previously only in the confirm modal).
                            <Box>
                                <SectionTitle>
                                    Pedido que se creará
                                </SectionTitle>
                                <Typography
                                    variant={'body2'}
                                    color={'text.secondary'}
                                    sx={{ mb: 1 }}
                                >
                                    Código: (se generará) · Fecha:{' '}
                                    {getBusinessDateFromInstant(
                                        new Date().toISOString(),
                                    )}
                                </Typography>
                                <OrderRequestPreview
                                    products={products}
                                    productDescriptions={productDescriptions}
                                />
                            </Box>
                        )}

                        {!complete &&
                        !isAcceptable &&
                        preview.blocking_reasons.length ? (
                            <Alert severity={'error'}>
                                <Typography variant={'body2'}>
                                    No se puede aceptar todavía:
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {preview.blocking_reasons.map(
                                        (reason, i) => (
                                            <li key={i}>{reason}</li>
                                        ),
                                    )}
                                </ul>
                            </Alert>
                        ) : null}
                    </Box>

                    {!complete ? (
                        /* Sticky bottom toolbar, matching the upsert forms'
                           action bar (RhFormWrapper). A completed quotation is a
                           read-only preview, so it has no acceptance toolbar. */
                        <Box
                            sx={{
                                position: 'sticky',
                                bottom: 0,
                                py: 2,
                                px: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 2,
                                bgcolor: 'background.paper',
                                borderTop: 1,
                                borderColor: 'divider',
                                zIndex: 1,
                            }}
                        >
                            <Button
                                variant={'contained'}
                                color={'success'}
                                type={'button'}
                                disabled={
                                    !isAcceptable ||
                                    accepting ||
                                    pedidoIncomplete
                                }
                                onClick={handleAccept}
                            >
                                {buttonLabel}
                            </Button>
                        </Box>
                    ) : null}
                </>
            )}
        </DModal>
    );
}
